import $ from 'jquery';
import Reason from './Reason';
import { i18n } from '../common/helpers';

/**
 * @typedef {Object} ReturnData
 * @property {string} action
 * @property {string} reason
 * @property {string} reason_code
 * @property {string} reason_category
 * @property {string} [wish]
 * @property {string} [free_input_field]
 * @property {string} [free_input_title]
 */

/**
 * @typedef {Object} Reason
 * @property {boolean} is_additional
 * @property {boolean} is_free
 * @property {boolean} is_fee_free
 * @property {boolean} free_input_field
 * @property {string} free_input_title
 * @property {string} reason_code
 * @property {string} reason_category
 * @property {boolean} required
 * @property {boolean} disclaimer
 * @property {string} disclaimer_text
 * @property {string} disclaimer_title
 * @property {boolean} is_last
 * @property {string} shown_name
 * @property {string} [free_input_title_shown]
 */

/**
 * Product class
 */
class SelectionProduct {
  /**
   * Class constructor
   * @param {jQuery} $element
   * @param {Record<string, Record<string, Reason> & { shown_name: string }>} reasons
   * @param {number} commentMaxLength
   * @param {{ [product]: string, [selected]: string, [button]: string, [image]: string, [label]: string }} options
   */
  constructor($element, reasons, commentMaxLength, options) {
    this.options = {
      product: SelectionProduct.PRODUCT,
      selected: SelectionProduct.SELECTED,
      button: SelectionProduct.BUTTON,
      image: SelectionProduct.IMAGE,
      label: SelectionProduct.LABEL,
      ...options,
    };

    /** @type {jQuery} */
    this.$el = $element;
    /** @type {jQuery} */
    this.$button = this.$el.find(this.options.button);
    /** @type {jQuery} */
    this.$image = this.$el.find(this.options.image);
    /** @type {jQuery} */
    this.$label = this.$el.find(this.options.label);

    this.fillData();

    /** @type {jQuery} */
    this.$quantity = this.$el.find('.js-quantity');
    /** @type {jQuery} */
    this.$reasonsWrap = this.$el.find('.selection__reasons');
    /** @type {jQuery} */
    this.$reasonsTitle = this.$el.find('.js-selection-reasons-title');
    this.reasonsGlobal = reasons;
    this.commentMaxLength = commentMaxLength;

    /** @type {{ id: number, quantity: number, reason: ReturnData, photos: string[][] }[]} */
    this.batches = [];
    this.nextBatchId = 0;
    this.handleBatchActions();

    // The reason popup's own markup already tells us whether photos are required,
    // without depending on the Popup instance existing yet (it's built after all
    // SelectionProduct instances in Step2.js)
    this.photosRequired = $('#reason-popup .attach-photos').length > 0;

    this.disabled = this.$button.prop('disabled');
    this.available = Number.parseInt(this.$el.data('available'), 10) || 0;

    this.initBatches();

    this.updateButton();
    this.handleClick();
  }

  /**
   * Remaining quantity that is not yet part of any batch
   * @returns {number}
   */
  remaining() {
    return this.available - (this.reasons ? this.reasons.length : 0);
  }

  /**
   * Absolute unit index (matching item_photos[id][index]) a batch's units start at.
   * A batch not yet added starts right after everything already committed.
   * @param {number} [batchId]
   * @returns {number}
   */
  getBatchStartIndex(batchId) {
    let index = 0;

    for (let i = 0; i < this.batches.length; i += 1) {
      if (this.batches[i].id === batchId) return index;
      index += this.batches[i].quantity;
    }

    return index;
  }

  /**
   * Build the initial batches from data pre-filled by the server
   * (e.g. after the form was re-rendered following a validation error).
   * Consecutive units sharing the same reason are grouped into a single batch,
   * but only when neither of them has photos attached - units with photos were
   * uploaded individually and must stay editable/removable on their own.
   */
  initBatches() {
    const reasons = this.$quantity.data('reasons') || [];
    const photos = this.$quantity.data('photos') || [];

    reasons.forEach((reason, index) => {
      const lastBatch = this.batches[this.batches.length - 1];
      const itemPhotos = photos[index] || [];
      const canGroup = lastBatch
        && JSON.stringify(lastBatch.reason) === JSON.stringify(reason)
        && !itemPhotos.length
        && lastBatch.photos.every((unitPhotos) => !unitPhotos.length);

      if (canGroup) {
        lastBatch.quantity += 1;
        lastBatch.photos.push(itemPhotos);
      } else {
        this.batches.push({
          id: (this.nextBatchId += 1),
          quantity: 1,
          reason,
          photos: [itemPhotos],
        });
      }
    });

    this.renderAll();
  }

  /**
   * Add a new batch of returned items with its own reason
   * @param {number} quantity
   * @param {ReturnData} reason
   * @param {Array<number|string>} [photoIds] IDs of photos uploaded for this batch
   */
  addBatch(quantity, reason, photoIds = []) {
    this.batches.push({
      id: (this.nextBatchId += 1),
      quantity,
      reason,
      photos: photoIds.length ? [photoIds] : [],
    });

    this.renderAll();
  }

  /**
   * Update an existing batch (used when editing it)
   * @param {number} id
   * @param {number} quantity
   * @param {ReturnData} reason
   * @param {Array<number|string>} [photoIds] IDs of photos uploaded for this batch
   */
  updateBatch(id, quantity, reason, photoIds = []) {
    const batch = this.batches.find((item) => item.id === id);

    if (!batch) return;

    batch.quantity = quantity;
    batch.reason = reason;
    batch.photos = photoIds.length ? [photoIds] : [];

    this.renderAll();
  }

  /**
   * Remove a batch, returning its quantity back to the available pool
   * @param {number} id
   */
  removeBatch(id) {
    this.batches = this.batches.filter((batch) => batch.id !== id);
    this.renderAll();
  }

  /**
   * Drop a photo id from a batch's stored data right after it was deleted server-side,
   * so a stale (now broken) reference doesn't linger if the popup is closed without saving
   * @param {number} batchId
   * @param {number|string} photoId
   */
  removeBatchPhoto(batchId, photoId) {
    const batch = this.batches.find((item) => item.id === batchId);

    if (!batch) return;

    batch.photos = batch.photos.map((unitPhotos) => unitPhotos.filter((id) => id !== photoId));
    this.renderAll();
  }

  /**
   * Bind edit/delete controls of the already rendered batches
   */
  handleBatchActions() {
    this.$reasonsWrap.on(`click${SelectionProduct.NAMESPACE}`, '.selection__batch-edit', (event) => {
      event.preventDefault();
      const id = Number($(event.currentTarget).closest('.selection__batch').data('batch-id'));
      const batch = this.batches.find((item) => item.id === id);
      if (batch) this.ask(batch);
    });

    this.$reasonsWrap.on(`click${SelectionProduct.NAMESPACE}`, '.selection__batch-remove', (event) => {
      event.preventDefault();
      const id = Number($(event.currentTarget).closest('.selection__batch').data('batch-id'));
      this.removeBatch(id);
    });
  }

  /**
   * Render a single batch: one wrapper with edit/delete controls, holding a
   * separate hidden-fields panel for every unit of the batch's quantity
   * (each unit needs its own panel, since Reason mutates its group elements
   * in place and sharing them between units would wipe each other's fields)
   * @param {{ id: number, quantity: number, reason: ReturnData, photos: string[][] }} batch
   * @param {number} startIndex
   */
  renderBatch(batch, startIndex) {
    const batchTemplate = $('#selection__batch-template').html();
    const unitTemplate = $('#selection__reasons-template').html();

    const $batch = $(batchTemplate).attr('data-batch-id', batch.id).appendTo(this.$reasonsWrap);
    const $unitsWrap = $batch.find('.selection__batch-units');

    $batch.find('.selection__batch-quantity').text(batch.quantity);
    $batch.find('.selection__batch-reason').text(SelectionProduct.formatReason(batch.reason));

    for (let i = 0; i < batch.quantity; i += 1) {
      const $panel = $(unitTemplate).data('reasons', batch.reason).appendTo($unitsWrap);
      // eslint-disable-next-line no-new
      new Reason($panel, {
        name: `item_returned_reasons_additional[${this.id}]`,
        index: startIndex + i,
        reasons: this.reasonsGlobal,
        commentMaxLength: this.commentMaxLength,
        isHidden: true,
      });
    }

    const $photosWrap = $batch.find('.selection__batch-photos');
    batch.photos.forEach((itemPhotos, i) => {
      (itemPhotos || []).forEach((photoId) => {
        $photosWrap.append(`<input type="hidden" name="item_photos[${this.id}][${startIndex + i}][]" value="${photoId}" />`);
      });
    });
  }

  /**
   * Rebuild the page from the retained batches, without losing any of them
   */
  renderAll() {
    // Clear the previous cart entry for this product before rebuilding it below
    this.deselect();

    this.$reasonsWrap.empty();

    this.reasons = [];
    let index = 0;
    this.batches.forEach((batch) => {
      for (let i = 0; i < batch.quantity; i += 1) this.reasons.push(batch.reason);
      this.renderBatch(batch, index);
      index += batch.quantity;
    });

    this.$quantity.val(this.reasons.length);
    this.quantity = this.reasons.length;

    this.$reasonsTitle
      .toggleClass('hidden', !this.reasons.length)
      .text(this.reasons.length ? i18n('Selected items to return ({0}):').replace('{0}', this.reasons.length) : '');

    if (this.reasons.length) {
      this.select();
    }

    this.updateAvailability();
  }

  /**
   * Disable the "Return" button once every available unit has been added to a batch -
   * but only when photos are required, since clicking it then always adds a new batch.
   * Otherwise, clicking always edits the single existing batch, so it must stay enabled.
   */
  updateAvailability() {
    this.$button.prop('disabled', this.disabled || (this.photosRequired && this.remaining() <= 0));
  }

  fillData() {
    this.id = this.$el.data('id');
    this.title = this.$el.find('.js-title').text();
    this.code = this.$el.find('.js-code').text();
    this.size = this.$el.find('.js-size').text();
    this.price = Number.parseFloat(this.$el.find('.js-price').text()) || 0;
  }

  getData() {
    return {
      id: this.id,
      title: this.title,
      code: this.code,
      size: this.size,
      price: this.price,
      reasons: this.reasons,
    };
  }

  /**
   * Update button state
   * @param {boolean} [isInit]
   */
  updateButton(isInit) {
    if (!isInit) {
      this.$el.toggleClass(this.options.selected, this.selected);
    }

    this.$label.each((_, el) => {
      let label;

      if (this.disabled) {
        label = 'disabled';
      } else if (this.selected) {
        label = 'deselect';
      } else {
        label = 'select';
      }

      $(el).text($(el).data(label));
    });
  }

  /**
   * Whether the product is selected
   * @returns {boolean}
   */
  isSelected() {
    return this.selected;
  }

  /**
   * Whether the product is disabled
   * @returns {boolean}
   */
  isDisabled() {
    return this.disabled;
  }

  /**
   * Select product
   */
  select() {
    this.selected = true;
    this.updateButton();
    this.$el.trigger('selected', [true, this.getData()]);
  }

  /**
   * Deselect product
   */
  deselect() {
    // NB set qty as 0 and remove hidden inputs here
    this.selected = false;
    this.updateButton();
    this.quantity = 0;
    this.$quantity.val(this.quantity);
    this.$el.trigger('selected', [false, this.getData()]);
  }

  /**
   * @typedef {Object} Batch
   * @property {number} id
   * @property {number} quantity
   * @property {ReturnData} reason
   * @property {string[][]} photos
   */

  /**
   * Show modal window with reasons for return
   * @param {Batch} [batch] batch to edit, if any
   */
  ask(batch) {
    const popup = $('#reason-popup').data('popup');

    // The backend only supports a single reason per item when photos aren't
    // required, so reopen the existing batch for editing instead of adding another
    if (!batch && !this.photosRequired && this.batches.length) {
      popup.open(this, this.batches[0]);
    } else {
      popup.open(this, batch);
    }
  }

  /**
   * Handle product button click
   */
  handleClick() {
    this.$button.add(this.$image).on(`click${SelectionProduct.NAMESPACE}`, (e) => {
      e.preventDefault();
      if (!this.disabled) {
        this.ask();
      }
    });
  }

  /**
   * Build a human-readable summary of a return reason (action / reason / wish)
   * @param {ReturnData} reason
   * @returns {string}
   */
  static formatReason(reason) {
    return ['action', 'reason', 'wish', 'free_input_field']
      .map((type) => reason[type])
      .filter(Boolean)
      .join(' / ');
  }
}

SelectionProduct.PRODUCT = '.selection__product';
SelectionProduct.SELECTED = 'selection__product_selected';
SelectionProduct.BUTTON = '.selection__button';
SelectionProduct.IMAGE = '.selection__image';
SelectionProduct.LABEL = '.selection__label';
SelectionProduct.NAMESPACE = '.product';

export default SelectionProduct;
